import { useEffect, useRef, useState, useCallback } from "react";
import pako from "pako";

/** ===================== helpers ===================== **/

function toPairs(levels) {
  if (!levels) return [];
  if (typeof levels === "string") {
    try { return toPairs(JSON.parse(levels)); } catch { return []; }
  }
  if (Array.isArray(levels)) {
    if (!levels.length) return [];
    if (Array.isArray(levels[0])) return levels.map(([p, q]) => [String(p), String(q)]);
    if (typeof levels[0] === "object") {
      return levels.map(o => {
        const p = o?.p ?? o?.price ?? o?.[0];
        const q = o?.q ?? o?.qty ?? o?.quantity ?? o?.[1];
        if (p == null || q == null) return null;
        return [String(p), String(q)];
      }).filter(Boolean);
    }
  }
  if (typeof levels === "object") {
    return Object.entries(levels).map(([p, q]) => [String(p), String(q)]);
  }
  return [];
}

async function decodeWsMessage(event) {
  try {
    const buf = event.data instanceof Blob
      ? new Uint8Array(await event.data.arrayBuffer())
      : new Uint8Array(event.data);
    const text = pako.ungzip(buf, { to: "string" });
    try { return JSON.parse(text); } catch { return text; }
  } catch {
    try { return typeof event.data === "string" ? event.data : String(event.data); } catch { return null; }
  }
}

function extractTickerPayload(data, symbol) {
  const obj = Array.isArray(data) ? (data.find(x => x?.symbol === symbol || x?.s === symbol) ?? data[0]) : data;
  if (!obj || typeof obj !== "object") return { symbol, lastPrice: "", volume24: "", bid: undefined, ask: undefined, _rawKeys: [] };

  const lastPrice =
    obj.lastPrice ?? obj.last ?? obj.close ?? obj.c ?? obj.p ?? obj.price ?? "";

  const volume24 =
    obj.volume ?? obj.baseVolume ?? obj.v ?? obj.quoteVolume ?? obj.qv ?? obj.q ?? "";

  const bid = obj.bestBid ?? obj.bid ?? obj.b ?? undefined;
  const ask = obj.bestAsk ?? obj.ask ?? obj.a ?? undefined;

  return {
    symbol,
    lastPrice: lastPrice !== undefined ? String(lastPrice) : "",
    volume24: volume24 !== undefined ? String(volume24) : "",
    bid, ask,
    _rawKeys: Object.keys(obj || {})
  };
}

/** ===================== hook ===================== **/

export const useBingxFutures = (symbol) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [tickerData, setTickerData] = useState(null);
  const [orderBook, setOrderBook] = useState({ asks: [], bids: [] });
  const [fundingRate] = useState(0); // публичный market-WS BingX не шлёт фандинг

  const wsRef = useRef(null);
  const animationFrameRef = useRef(null);

  const orderBookRef = useRef({ asks: [], bids: [] });
  const hasSnapshotRef = useRef(false);
  const lastUpdateIdRef = useRef(null);
  const connectGuardRef = useRef(false);
  const reconnectTimerRef = useRef(null);

  const WS_URL = "wss://open-api-swap.bingx.com/swap-market";
  const ENABLE_AUTO_RECONNECT = false;

  const log = (...args) => console.log("🟠 [BINGX FUTURES]", ...args);

  const applyDeltas = useCallback((levels, side) => {
    const pairs = toPairs(levels);
    const base = orderBookRef.current[side];
    const next = [...base];

    pairs.forEach(([price, quantity]) => {
      const pn = parseFloat(price);
      const qn = parseFloat(quantity);
      const idx = next.findIndex(([p]) => parseFloat(p) === pn);

      if (qn === 0) {
        if (idx !== -1) next.splice(idx, 1);
      } else {
        if (idx !== -1) next[idx] = [price, quantity];
        else next.push([price, quantity]);
      }
    });

    orderBookRef.current[side] =
      side === "asks"
        ? next.sort((a, b) => parseFloat(a[0]) - parseFloat(b[0])).slice(0, 20)
        : next.sort((a, b) => parseFloat(b[0]) - parseFloat(a[0])).slice(0, 20);

    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(() => {
      setOrderBook({ ...orderBookRef.current });
    });
  }, []);

  const subscribeAll = useCallback((ws, sym) => {
    const subs = [
      { id: `incr-${sym}`,  reqType: "sub", dataType: `${sym}@incrDepth` },
      { id: `tick-${sym}`,  reqType: "sub", dataType: `${sym}@ticker`    },
      { id: `last-${sym}`,  reqType: "sub", dataType: `${sym}@lastPrice` },
    ];
    subs.forEach(s => {
      ws.send(JSON.stringify(s));
      log("→ SUB", s);
    });
  }, []);

  const handleMessage = useCallback(async (event) => {
    try {
      const msg = await decodeWsMessage(event);
      if (msg == null) return;

      // heartbeat
      if (msg === "Ping") { wsRef.current?.send("Pong"); log("↔ Pong"); return; }
      if (typeof msg !== "object") { log("txt:", String(msg).slice(0, 200)); return; }

      // sub ack
      if (msg.code === 0 && msg.id) { log("✅ sub ack:", msg.id); return; }

      const data = msg.data ?? msg.result ?? msg;

      // ====== TICKER & LASTPRICE ======
      const isTicker    = typeof msg.dataType === "string" && msg.dataType.endsWith("@ticker");
      const isLastPrice = typeof msg.dataType === "string" && msg.dataType.endsWith("@lastPrice");

      if (isTicker || isLastPrice || data?.last != null || data?.lastPrice != null || data?.c != null) {
        const extracted = extractTickerPayload(data, symbol);

        if (isLastPrice) {
          // только цена, не трогаем volume/bid/ask
          setTickerData(prev => ({
            ...(prev || {}),
            symbol: extracted.symbol,
            lastPrice: extracted.lastPrice || prev?.lastPrice || ""
          }));
        } else if (isTicker) {
          // полная строка: обновляем всё, но не затираем пустыми
          setTickerData(prev => ({
            ...(prev || {}),
            symbol: extracted.symbol,
            lastPrice: extracted.lastPrice || prev?.lastPrice || "",
            volume24: extracted.volume24 !== "" ? extracted.volume24 : (prev?.volume24 || ""),
            bid: extracted.bid ?? prev?.bid,
            ask: extracted.ask ?? prev?.ask
          }));
        } else {
          // неизвестная форма, ведём себя безопасно
          setTickerData(prev => ({
            ...(prev || {}),
            symbol: extracted.symbol,
            lastPrice: extracted.lastPrice || prev?.lastPrice || "",
            volume24: extracted.volume24 !== "" ? extracted.volume24 : (prev?.volume24 || prev?.volume || ""),
            bid: extracted.bid ?? prev?.bid,
            ask: extracted.ask ?? prev?.ask
          }));
        }
      }

      // ====== DEPTH (snapshot + incr) ======
      if (data?.action === "all" || data?.action === "update" || data?.asks || data?.bids || data?.a || data?.b) {
        const asks = toPairs(data?.asks ?? data?.a);
        const bids = toPairs(data?.bids ?? data?.b);
        const lastId = data?.lastUpdateId ?? data?.u ?? data?.U ?? null;

        if (data?.action === "all" || !hasSnapshotRef.current) {
          orderBookRef.current = {
            asks: asks.sort((x, y) => parseFloat(x[0]) - parseFloat(y[0])).slice(0, 20),
            bids: bids.sort((x, y) => parseFloat(y[0]) - parseFloat(x[0])).slice(0, 20),
          };
          hasSnapshotRef.current = true;
          lastUpdateIdRef.current = lastId ?? lastUpdateIdRef.current;
          setOrderBook({ ...orderBookRef.current });
          log("📊 snapshot set. lastId=", lastId, "asks/bids:", orderBookRef.current.asks.length, orderBookRef.current.bids.length);
          return;
        }

        if (lastId != null && lastUpdateIdRef.current != null && lastId <= lastUpdateIdRef.current) {
          log("⏭ stale depth update skipped. lastId=", lastId, "local=", lastUpdateIdRef.current);
          return;
        }
        if (lastId != null) lastUpdateIdRef.current = lastId;

        if (asks.length) applyDeltas(asks, "asks");
        if (bids.length) applyDeltas(bids, "bids");
      }
    } catch (e) {
      log("❌ parse err:", e);
    }
  }, [applyDeltas, symbol]);

  useEffect(() => {
    if (!symbol) return;

    setIsConnected(false);
    setError(null);
    setTickerData(null);
    setOrderBook({ asks: [], bids: [] });
    orderBookRef.current = { asks: [], bids: [] };
    hasSnapshotRef.current = false;
    lastUpdateIdRef.current = null;

    if (connectGuardRef.current) {
      log("⛔ skip connect: guard");
      return;
    }
    connectGuardRef.current = true;

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    ws.binaryType = "arraybuffer";

    ws.onopen = () => {
      log("🔗 open:", WS_URL, "symbol:", symbol);
      setIsConnected(true);
      subscribeAll(ws, symbol); // символ уже в формате XXX-USDT
    };

    ws.onmessage = handleMessage;

    ws.onerror = (e) => {
      log("❌ WS error event:", e);
      setError(e?.message || "WS error");
      setIsConnected(false);
    };

    ws.onclose = (e) => {
      log("🔌 close:", { code: e?.code, reason: e?.reason, wasClean: e?.wasClean });
      setIsConnected(false);
      connectGuardRef.current = false;

      if (ENABLE_AUTO_RECONNECT) {
        reconnectTimerRef.current = setTimeout(() => {
          log("↻ reconnecting…");
          connectGuardRef.current = false;
        }, 1500);
      }
    };

    return () => {
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
      try { ws.close(1000, "component unmount"); } catch {}
      if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null; }
      connectGuardRef.current = false;
      wsRef.current = null;
      log("🧹 cleanup done");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  return { isConnected, error, orderBook, tickerData, fundingRate };
};
